import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import toast from 'react-hot-toast';
import { downloadFileFromStorage } from '../../../dashboard.service';
import { exportXml } from '../../../../helper/api.helper';
import { subscribeToProjectStatus } from '../../../../helper/project.service';

const TABS = [
  { id: 'video', label: 'Video', Icon: VideoFileOutlinedIcon },
  { id: 'subtitles', label: 'Subtitles', Icon: SubtitlesOutlinedIcon },
  { id: 'presentation', label: 'Presentation', Icon: SlideshowIcon },
  { id: 'timeline', label: 'Timeline', Icon: ViewTimelineOutlinedIcon }
];

const sanitizeFilename = (name) => {
  if (!name || !name.trim()) return null;
  return name
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const Download = ({ videoUrl, isMobile, isTablet, projectName, projectId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState('video');
  const [timelineFormat, setTimelineFormat] = useState('fcpxml');
  const [includeMediaFiles, setIncludeMediaFiles] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [xmlExportUrl, setXmlExportUrl] = useState(null);
  const exportStatusUnsubscribeRef = useRef(null);
  const projectStatusUnsubscribeRef = useRef(null);

  const baseName = sanitizeFilename(projectName) || 'video';

  useEffect(() => {
    if (!projectId) return undefined;

    if (projectStatusUnsubscribeRef.current) {
      projectStatusUnsubscribeRef.current();
      projectStatusUnsubscribeRef.current = null;
    }

    projectStatusUnsubscribeRef.current = subscribeToProjectStatus(
      projectId,
      (projectData) => {
        setXmlExportUrl(projectData?.fcpxml?.xmlExport || null);
      },
      (error) => {
        console.error('Project subscription error:', error);
      }
    );

    return () => {
      if (exportStatusUnsubscribeRef.current) {
        exportStatusUnsubscribeRef.current();
        exportStatusUnsubscribeRef.current = null;
      }
      if (projectStatusUnsubscribeRef.current) {
        projectStatusUnsubscribeRef.current();
        projectStatusUnsubscribeRef.current = null;
      }
    };
  }, [projectId]);

  const handleOpen = (e) => {
    setActiveTab('video');
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const srtUrl = videoUrl
    ? videoUrl.replace(/\.mp4(\?.*)?$/, (match, qs) => `.srt${qs || ''}`)
    : null;

  const handleDownload = async (url, filename) => {
    if (!url) return;
    try {
      setDownloading(true);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      handleClose();
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePPTDownload = async () => {
    try {
      setDownloading(true);
      const filePath = `uploads/${projectId}/pptx/file.pptx`;
      await downloadFileFromStorage(filePath, `${projectName}.pptx`);
      handleClose();
    } catch (error) {
      console.error('PPT download error:', error);
      toast.error('Failed to download presentation. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleTimelineDownload = async () => {
    if (!projectId) return;

    if (xmlExportUrl) {
      await handleDownload(xmlExportUrl, `${baseName}-timeline.zip`);
      return;
    }

    try {
      setDownloading(true);
      if (exportStatusUnsubscribeRef.current) {
        exportStatusUnsubscribeRef.current();
        exportStatusUnsubscribeRef.current = null;
      }

      const response = await exportXml(projectId);
      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(result?.message || 'Failed to start timeline export.');
      }

      toast.loading('Preparing timeline export...', { id: 'timeline-export' });

      exportStatusUnsubscribeRef.current = subscribeToProjectStatus(
        projectId,
        async (projectData) => {
          const exportStatus = projectData?.fcpxml?.ExportStatus;
          const xmlUrl = projectData?.fcpxml?.xmlExport;

          if (typeof exportStatus !== 'string') {
            return;
          }

          if (exportStatus.toUpperCase() === 'COMPLETED' && xmlUrl) {
            if (exportStatusUnsubscribeRef.current) {
              exportStatusUnsubscribeRef.current();
              exportStatusUnsubscribeRef.current = null;
            }

            toast.success('Timeline export ready.', { id: 'timeline-export' });
            await handleDownload(xmlUrl, `${baseName}-timeline.zip`);
          }

          if (exportStatus.toUpperCase() === 'FAILED') {
            if (exportStatusUnsubscribeRef.current) {
              exportStatusUnsubscribeRef.current();
              exportStatusUnsubscribeRef.current = null;
            }

            toast.error('Timeline export failed. Please try again.', { id: 'timeline-export' });
            setDownloading(false);
          }
        },
        (error) => {
          console.error('Timeline export subscription error:', error);
          if (exportStatusUnsubscribeRef.current) {
            exportStatusUnsubscribeRef.current();
            exportStatusUnsubscribeRef.current = null;
          }
          toast.error('Failed to track timeline export.', { id: 'timeline-export' });
          setDownloading(false);
        }
      );
    } catch (error) {
      console.error('Timeline download error:', error);
      toast.error('Failed to export timeline. Please try again.');
      setDownloading(false);
    }
  };

  const open = Boolean(anchorEl);

  const tabContent = {
    video: {
      Icon: VideoFileOutlinedIcon,
      description: 'Download your exported video file.',
      buttonLabel: 'Download Video (.mp4)',
      onDownload: () => handleDownload(videoUrl, `${baseName}.mp4`),
      disabled: !videoUrl
    },
    subtitles: {
      Icon: SubtitlesOutlinedIcon,
      description: 'Download the SubRip subtitle file for your video.',
      buttonLabel: 'Download SubRip Titles (.srt)',
      onDownload: () => handleDownload(srtUrl, `${baseName}.srt`),
      disabled: !srtUrl
    },
    presentation: {
      Icon: SlideshowIcon,
      description: 'Download your PowerPoint presentation.',
      buttonLabel: 'Download Presentation (.pptx)',
      onDownload: () => handlePPTDownload(),
      disabled: !projectId
    },
    timeline: {
      Icon: ViewTimelineOutlinedIcon,
      description: 'Export your timeline in edit-friendly formats.',
      buttonLabel: xmlExportUrl ? 'Download Timeline (.zip)' : 'Export Timeline',
      onDownload: handleTimelineDownload,
      disabled: false
    }
  };

  const active = tabContent[activeTab];
  const activeLabel = TABS.find((tab) => tab.id === activeTab)?.label || '';

  return (
    <>
      {/* ─── Trigger Button ─── */}
      <Button
        onClick={handleOpen}
        aria-label="Download"
        startIcon={
          !isMobile ? <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} /> : undefined
        }
        sx={{
          background: 'var(--white)',
          borderRadius: '8px',
          height: isMobile ? '40px' : isTablet ? '40px' : '46px',
          minWidth: isMobile ? '40px' : 'auto',
          width: isMobile ? '40px' : 'auto',
          px: isMobile ? 0 : isTablet ? '12px' : '16px',
          color: 'var(--btn-purple-color)',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: isTablet ? '14px' : '15px',
          boxShadow: 'none',
          '&:hover': {
            background: 'var(--white)',
            opacity: 0.85,
            boxShadow: 'none'
          }
        }}>
        {isMobile ? <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} /> : 'Download'}
      </Button>

      {/* ─── Popover ─── */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            background: 'var(--white)',
            color: 'var(--gray-darkest)',
            width: isMobile ? '95vw' : 'auto',
            minWidth: isMobile ? 'unset' : 280,
            p: 0,
            overflow: 'hidden',
            transition: 'all 0.2s ease'
          }
        }}>
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: 'var(--gray-darkest)' }}>
            Download
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close"
            sx={{
              color: '#999',
              '&:hover': { color: 'var(--gray-darkest)', background: '#f5f5f5' }
            }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'var(--border-gray)' }} />

        {/* Tab bar */}
        <Box
          sx={{
            display: 'flex',
            px: 2,
            pt: 1.5,
            gap: 0.5,
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
          {TABS.map(({ id, label, Icon }) => {
            const isActive = activeTab === id;
            return (
              <Box
                key={id}
                onClick={() => setActiveTab(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: isMobile ? 1 : 1.5,
                  py: 0.75,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: isMobile ? '12px' : '13px',
                  color: isActive ? 'var(--btn-purple-color)' : '#666',
                  background: isActive ? 'rgba(48, 26, 255, 0.07)' : 'transparent',
                  userSelect: 'none',
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                  '&:hover': { background: isActive ? 'rgba(48, 26, 255, 0.1)' : '#f5f5f5' }
                }}>
                <Icon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontWeight: 'inherit', fontSize: 'inherit', color: 'inherit' }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Tab content */}
        <Box sx={{ px: 2.5, pt: 2, pb: 2, minHeight: 72 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <active.Icon sx={{ fontSize: 18, color: 'var(--btn-purple-color)' }} />
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: 'var(--gray-darkest)' }}>
              {activeLabel}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
            {active.description}
          </Typography>

          {activeTab === 'timeline' && (
            <>
              <Divider sx={{ borderColor: 'var(--border-gray)', my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                  }}>
                  <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 600 }}>
                    Format
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 190 }}>
                    <Select
                      value={timelineFormat}
                      onChange={(e) => setTimelineFormat(e.target.value)}>
                      <MenuItem sx={{ fontSize: 13, py: 0.75, minHeight: 34 }} value="fcpxml">
                        Final Cut Pro X (.fcpxml)
                      </MenuItem>
                      {/* <MenuItem sx={{ fontSize: 13, py: 0.75, minHeight: 34 }} value="premiere">
                        Premiere (.xml)
                      </MenuItem>
                      <MenuItem sx={{ fontSize: 13, py: 0.75, minHeight: 34 }} value="resolve">
                        DaVinci Resolve (.xml)
                      </MenuItem> */}
                    </Select>
                  </FormControl>
                </Box>

                {/* <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                  }}>
                  <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 600 }}>
                    Include media files in export
                  </Typography>
                  <Switch
                    checked={includeMediaFiles}
                    onChange={(e) => setIncludeMediaFiles(e.target.checked)}
                  />
                </Box> */}
              </Box>
            </>
          )}
        </Box>

        <Divider sx={{ borderColor: 'var(--border-gray)' }} />

        {/* Footer — download button */}
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={
              downloading ? (
                <CircularProgress size={16} sx={{ color: 'var(--white)' }} />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            onClick={active.onDownload}
            disabled={active.disabled || downloading}
            sx={{
              background: 'var(--btn-purple-color)',
              color: 'var(--white)',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              py: 1,
              boxShadow: 'none',
              '&:hover': {
                background: 'var(--btn-purple-color)',
                opacity: 0.88,
                boxShadow: '0 4px 14px rgba(48, 26, 255, 0.35)'
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#aaa'
              }
            }}>
            {active.buttonLabel}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default Download;
